import { Box, MenuItem, TextField, Typography } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { GetCustomerAddressesQuery } from '../../../graphql/magento'
import useIsMounted from '../../../hooks/useIsMounted'

const StyledEllipsisBlock = styled(Typography)`
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
`

export interface SelectSavedAddressProps {
  customerAddresses: NonNullable<GetCustomerAddressesQuery['customer']>['addresses']
  defaultAddressId?: string
  loading: boolean
  onSelect: (selectedAddressId?: string) => void
  handleAddNewAddress: () => void
}

const SelectSavedAddress: React.FC<SelectSavedAddressProps> = ({
  customerAddresses,
  defaultAddressId,
  loading,
  onSelect,
  handleAddNewAddress,
}) => {
  const [selectedAddressId, setSelectedAddressId] = useState(defaultAddressId || '')
  const isMounted = useIsMounted()
  // maintain value in ref for reference in useEffect without dependency
  const defaultAddressIdRef = useRef(defaultAddressId || '')

  useEffect(() => {
    const value = defaultAddressId || ''
    // console.log(`setValue[selectedSavedAddressId](${value})`)
    defaultAddressIdRef.current = value
    setSelectedAddressId(value)
  }, [defaultAddressId])

  useEffect(() => {
    // do not execute callback if not mounted or selected address equal to prop
    if (!isMounted.current || selectedAddressId === defaultAddressIdRef.current) {
      return
    }
    // console.log(`onSelect(${selectedAddressId})`)
    onSelect(selectedAddressId)
  }, [isMounted, onSelect, selectedAddressId])

  // use standalone controlled component for simplicity
  return (
    <TextField
      disabled={loading}
      fullWidth
      InputProps={{ disableUnderline: true }}
      label={loading ? 'Loading saved addresses...' : 'Click to choose saved address...'}
      name="selectedSavedAddressId"
      onChange={(e) => setSelectedAddressId(e.target.value)}
      select
      value={selectedAddressId}
      variant="filled"
    >
      <MenuItem value="" onClick={handleAddNewAddress}>
        Add new address
      </MenuItem>
      {!!customerAddresses &&
        customerAddresses.map((option) => {
          const { id, firstname, lastname, street, default_billing, default_shipping } =
            option || {}
          const [street1] = street || []
          return id && street1 ? (
            <MenuItem key={id} value={String(id)}>
              <Box>
                <Box fontWeight="fontWeightBold">
                  <StyledEllipsisBlock variant="inherit">
                    {firstname} {lastname}{' '}
                    {default_shipping && default_billing ? (
                      <Typography variant="caption">
                        (Default Shipping and Billing Address)
                      </Typography>
                    ) : (
                      (default_shipping && (
                        <Typography variant="caption">(Default Shipping Address)</Typography>
                      )) ||
                      (default_billing && (
                        <Typography variant="caption">(Default Billing Address)</Typography>
                      ))
                    )}
                  </StyledEllipsisBlock>
                </Box>
                <StyledEllipsisBlock variant="inherit">{street1}</StyledEllipsisBlock>
              </Box>
            </MenuItem>
          ) : null
        })}
    </TextField>
  )
}

export default SelectSavedAddress
